import { useEffect, useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { useHomeIQ } from "@/homeiq/state/HomeIQContext";
import { fmtINR, fmtINRCompact } from "@/homeiq/services/format";
import {
    BANK_FOIR_BANDS,
    BANK_DISCLAIMER,
    COST_BREAKDOWN,
    FOIR_COMPARISON,
} from "@/homeiq/services/placeholders";

const EASE = [0.16, 1, 0.3, 1];

function Reveal({ children, delay = 0, className = "" }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay, ease: EASE }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

function SectionHead({ eyebrow, title, sub }) {
    return (
        <div className="mb-8">
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">
                — {eyebrow}
            </p>
            <h2 className="font-display font-extrabold text-3xl sm:text-4xl text-[#0A1F44] tracking-tight">
                {title}
            </h2>
            {sub && <p className="mt-3 text-[#0A1F44]/65 max-w-2xl text-sm md:text-base">{sub}</p>}
        </div>
    );
}

function SalaryRow() {
    const { salary, setSalary, obligations, setObligations } = useHomeIQ();
    return (
        <Reveal>
            <div data-testid="aff-salary-row" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8 shadow-[0_30px_80px_-50px_rgba(10,31,68,0.25)]">
                <div className="grid md:grid-cols-2 gap-6">
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Monthly take-home salary</label>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-[#0A1F44]/50">₹</span>
                            <input
                                type="number"
                                value={salary}
                                onChange={(e) => setSalary(Number(e.target.value) || 0)}
                                data-testid="aff-salary-input"
                                className="bg-transparent flex-1 text-3xl font-display font-extrabold text-[#0A1F44] focus:outline-none"
                            />
                        </div>
                        <input
                            type="range"
                            min={50000}
                            max={1000000}
                            step={5000}
                            value={salary}
                            onChange={(e) => setSalary(Number(e.target.value))}
                            data-testid="aff-salary-slider"
                            className="mt-4 w-full accent-[#1E6BFF]"
                        />
                        <div className="text-xs text-[#0A1F44]/50 mt-1">After tax, in hand</div>
                    </div>
                    <div>
                        <label className="text-[11px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Existing monthly obligations</label>
                        <div className="mt-2 flex items-baseline gap-2">
                            <span className="text-[#0A1F44]/50">₹</span>
                            <input
                                type="number"
                                value={obligations}
                                onChange={(e) => setObligations(Number(e.target.value) || 0)}
                                data-testid="aff-obligations-input"
                                className="bg-transparent flex-1 text-3xl font-display font-extrabold text-[#0A1F44] focus:outline-none"
                            />
                        </div>
                        <input
                            type="range"
                            min={0}
                            max={salary}
                            step={1000}
                            value={Math.min(obligations, salary)}
                            onChange={(e) => setObligations(Number(e.target.value))}
                            data-testid="aff-obligations-slider"
                            className="mt-4 w-full accent-[#1E6BFF]"
                        />
                        <div className="text-xs text-[#0A1F44]/50 mt-1">Car loan, personal loan, credit card minimums · capped at salary</div>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

function BankRuleToggle() {
    const { bankRule, setBankRule } = useHomeIQ();
    const options = [
        { value: 50, label: "Conservative", sub: "50%" },
        { value: 55, label: "Standard", sub: "55%" },
        { value: 60, label: "Aggressive", sub: "60%" },
    ];
    return (
        <Reveal delay={0.08}>
            <div data-testid="aff-bank-rule-toggle" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-5 md:p-6">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">Bank rule (FOIR)</div>
                        <div className="text-sm text-[#0A1F44]/65 mt-1">How aggressively the bank lets you borrow.</div>
                    </div>
                    <div className="inline-flex items-center rounded-full bg-[#0A1F44]/5 p-1 flex-wrap md:flex-nowrap">
                        {options.map((o) => {
                            const active = bankRule === o.value;
                            return (
                                <button
                                    key={o.value}
                                    type="button"
                                    onClick={() => setBankRule(o.value)}
                                    data-testid={`aff-bank-rule-${o.value}`}
                                    className={`px-3 md:px-4 py-2 md:py-2 rounded-full text-xs md:text-[13px] font-semibold whitespace-nowrap transition-colors min-h-[40px] flex items-center ${active ? "bg-[#0A1F44] text-white" : "text-[#0A1F44]/65 hover:text-[#0A1F44]"}`}
                                >
                                    <span className="md:hidden">{o.sub}</span>
                                    <span className="hidden md:inline">{o.label}<span className="ml-1.5 text-[#1E6BFF]">{o.sub}</span></span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

function ControlsPanel() {
    const { downPayment, setDownPayment, tenure, setTenure, rate, setRate } = useHomeIQ();
    const controls = [
        { label: "Down payment", value: fmtINR(downPayment), slider: downPayment, set: setDownPayment, min: 500000, max: 50000000, step: 100000, key: "downPayment" },
        { label: "Tenure", value: `${tenure} years`, slider: tenure, set: setTenure, min: 5, max: 30, step: 1, key: "tenure" },
        { label: "Interest rate", value: `${rate.toFixed(2)}%`, slider: rate, set: setRate, min: 6.5, max: 12, step: 0.1, key: "rate" },
    ];
    return (
        <Reveal delay={0.1}>
            <div data-testid="aff-controls-panel" className="grid md:grid-cols-3 gap-3">
                {controls.map((c) => (
                    <div key={c.key} className="rounded-2xl border border-[#0A1F44]/10 bg-white p-4">
                        <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/60 font-semibold">{c.label}</div>
                        <div className="mt-1 text-xl font-display font-extrabold text-[#0A1F44] tabular-nums">{c.value}</div>
                        <input
                            type="range"
                            min={c.min}
                            max={c.max}
                            step={c.step}
                            value={c.slider}
                            onChange={(e) => c.set(Number(e.target.value))}
                            data-testid={`aff-control-${c.key}`}
                            className="mt-3 w-full accent-[#1E6BFF]"
                        />
                    </div>
                ))}
            </div>
        </Reveal>
    );
}

function AffordabilityAlert() {
    const { affordabilityOutputs } = useHomeIQ();
    if (!affordabilityOutputs.is_affordability_alert) return null;
    return (
        <Reveal delay={0.12}>
            <div
                data-testid="aff-alert-banner"
                role="alert"
                className="rounded-2xl border border-[#dc2626]/30 bg-[#fef2f2] p-4 md:p-5 flex items-start gap-4"
            >
                <div className="mt-0.5 w-2 h-2 rounded-full bg-[#dc2626] shrink-0" aria-hidden />
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-[#dc2626] font-semibold">Affordability alert</div>
                    <div className="mt-1 text-sm text-[#0A1F44]">
                        Your current inputs cross at least one guardrail (FOIR {affordabilityOutputs.foir_status.toLowerCase()}, low down payment, or high existing obligations). Consider adjusting before proceeding.
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

function ResultsStrip() {
    const { affordabilityOutputs, bankRule, tenure, rate } = useHomeIQ();
    const {
        max_affordable_property,
        emi_on_eligible_loan,
        eligible_loan,
        segment,
    } = affordabilityOutputs;
    return (
        <Reveal delay={0.15}>
            <div data-testid="aff-results-strip" className="rounded-3xl bg-[#0A1F44] text-white p-6 md:p-10 relative overflow-hidden">
                <div className="absolute inset-0 blueprint-grid opacity-[0.06] pointer-events-none" />
                <div className="relative flex items-center justify-between gap-3 mb-6">
                    <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Your affordable bracket</div>
                    <span
                        data-testid="aff-segment-badge"
                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#1E6BFF]/15 border border-[#1E6BFF]/40 text-[11px] font-semibold text-[#7FA8FF]"
                    >
                        <span className="w-1.5 h-1.5 rounded-full bg-[#1E6BFF]" aria-hidden />
                        {segment}
                    </span>
                </div>
                <div className="grid md:grid-cols-3 gap-6 relative">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Home you can afford</div>
                        <div className="mt-2 font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tight">{fmtINRCompact(max_affordable_property)}</div>
                        <div className="text-sm text-white/60 mt-1">at {bankRule}% FOIR · {tenure} yrs · {rate.toFixed(2)}%</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Indicative EMI</div>
                        <div className="mt-2 font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tight">₹{Math.round(emi_on_eligible_loan).toLocaleString("en-IN")}</div>
                        <div className="text-sm text-white/60 mt-1">monthly · principal + interest</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Loan eligibility</div>
                        <div className="mt-2 font-display font-black text-3xl sm:text-4xl md:text-5xl tracking-tight">{fmtINRCompact(eligible_loan)}</div>
                        <div className="text-sm text-white/60 mt-1">principal · within {bankRule}% FOIR</div>
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

function FOIRStatus() {
    const { bankRule, affordabilityOutputs } = useHomeIQ();
    const { foir_usage_pct, obligation_burden_pct, foir_status } = affordabilityOutputs;
    const used = Math.min(100, Math.round(foir_usage_pct));
    const burdenPct = Math.min(100, Math.round(obligation_burden_pct));
    const overThreshold = burdenPct > 30;
    const statusTone =
        foir_status === "Critical" ? "text-[#dc2626]" :
        foir_status === "High" ? "text-[#d97706]" :
        "text-[#1E6BFF]";
    return (
        <Reveal delay={0.05}>
            <div data-testid="aff-foir-status" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8 space-y-6">
                {/* FOIR usage */}
                <div>
                    <div className="flex items-baseline justify-between mb-3">
                        <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">Your FOIR usage</h3>
                        <span className={`text-sm font-semibold ${statusTone}`}>{used}% of {bankRule}% · {foir_status}</span>
                    </div>
                    <div className="relative h-3 rounded-full bg-[#0A1F44]/8 overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#1E6BFF] to-[#5B8DFF] rounded-full" style={{ width: `${used}%` }} />
                        <div className="absolute inset-y-0 border-l-2 border-dashed border-[#0A1F44]/40" style={{ left: `${bankRule}%` }} />
                    </div>
                    <div className="mt-3 text-xs text-[#0A1F44]/60">Bank guardrail: keep total EMI commitments under {bankRule}% of take-home.</div>
                </div>

                {/* Obligation burden */}
                <div data-testid="aff-obligation-burden" className="pt-4 border-t border-[#0A1F44]/8">
                    <div className="flex items-baseline justify-between mb-3">
                        <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">Obligation burden</h3>
                        <span className={`text-sm font-semibold ${overThreshold ? "text-[#dc2626]" : "text-[#0A1F44]/65"}`}>
                            {burdenPct}% of salary
                        </span>
                    </div>
                    <div className="relative h-3 rounded-full bg-[#0A1F44]/8 overflow-hidden">
                        <div
                            className={`absolute inset-y-0 left-0 rounded-full ${overThreshold ? "bg-[#dc2626]" : "bg-[#0A1F44]"}`}
                            style={{ width: `${burdenPct}%` }}
                        />
                        <div className="absolute inset-y-0 border-l-2 border-dashed border-[#d97706]" style={{ left: "30%" }} />
                    </div>
                    <div className="mt-3 text-xs text-[#0A1F44]/60">
                        {overThreshold
                            ? "Above 30% — existing debt is eating into the headroom banks want to see."
                            : "Below the 30% threshold banks treat as comfortable."}
                    </div>
                </div>
            </div>
        </Reveal>
    );
}

function CostBreakdown() {
    const { affordabilityOutputs } = useHomeIQ();
    const { max_affordable_property } = affordabilityOutputs;
    const totalPct = COST_BREAKDOWN.reduce((a, c) => a + c.pct, 0);
    const addOn = max_affordable_property * (totalPct / 100);
    return (
        <Reveal delay={0.1}>
            <div data-testid="aff-cost-breakdown" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                    <h3 className="font-display font-extrabold text-2xl text-[#0A1F44] mb-1">True cost of ownership</h3>
                <p className="text-sm text-[#0A1F44]/65 mb-5">Beyond the sticker price.</p>
                <div className="space-y-3">
                    {COST_BREAKDOWN.map((c) => (
                        <div key={c.label} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4">
                            <div className="flex-1 text-sm text-[#0A1F44] flex items-baseline justify-between sm:justify-start">
                                <span>{c.label}</span>
                                <span className="sm:hidden text-sm font-semibold text-[#0A1F44] tabular-nums">{c.pct}%</span>
                            </div>
                            <div className="hidden sm:block w-40 h-1.5 rounded-full bg-[#0A1F44]/8 overflow-hidden">
                                <div className="h-full bg-[#1E6BFF]" style={{ width: `${(c.pct / 10) * 100}%` }} />
                            </div>
                            <div className="hidden sm:block w-12 text-right text-sm font-semibold text-[#0A1F44] tabular-nums">{c.pct}%</div>
                        </div>
                    ))}
                </div>
                <div className="mt-5 pt-4 border-t border-[#0A1F44]/8 flex items-center justify-between">
                    <span className="text-sm text-[#0A1F44]/65">Estimated add-on on a {fmtINRCompact(max_affordable_property)} home</span>
                    <span className="font-display font-extrabold text-2xl text-[#0A1F44]">{fmtINRCompact(addOn)}</span>
                </div>
            </div>
        </Reveal>
    );
}

function FOIRComparisonTable() {
    const { affordabilityOutputs } = useHomeIQ();
    const rows = affordabilityOutputs.foir_comparison_rows;
    const STATUS_COLOR = {
        conservative: "#16a34a",
        balanced: "#1E6BFF",
        stretched: "#d97706",
        risky: "#dc2626",
    };
    return (
        <Reveal delay={0.05}>
            <div data-testid="aff-foir-table" className="rounded-3xl border border-[#0A1F44]/10 bg-white overflow-hidden">
                <div className="px-6 md:px-8 pt-6">
                    <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">If you stretched a little more</h3>
                    <p className="text-sm text-[#0A1F44]/65 mt-1 mb-4">Same salary, different FOIR — what changes.</p>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-[#0A1F44]/4 text-[#0A1F44]/65 text-[11px] uppercase tracking-[0.2em]">
                            <tr>
                                <th className="text-left px-6 md:px-8 py-3 font-semibold">FOIR rule</th>
                                <th className="text-right px-4 py-3 font-semibold">EMI</th>
                                <th className="text-right px-4 py-3 font-semibold">Home</th>
                                <th className="text-left px-6 md:px-8 py-3 font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.rule} className="border-t border-[#0A1F44]/8 hover:bg-[#1E6BFF]/4 transition-colors">
                                    <td className="px-6 md:px-8 py-4 font-semibold text-[#0A1F44]">{r.rule}% FOIR</td>
                                    <td className="px-4 py-4 text-right tabular-nums text-[#0A1F44]">{fmtINRCompact(r.emi)}</td>
                                    <td className="px-4 py-4 text-right tabular-nums text-[#0A1F44]">{fmtINRCompact(r.home)}</td>
                                    <td className="px-6 md:px-8 py-4 capitalize" style={{ color: STATUS_COLOR[r.status] }}>{r.status}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </Reveal>
    );
}

function BankBands() {
    return (
        <Reveal delay={0.1}>
            <div data-testid="aff-bank-bands" className="rounded-3xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-6 md:p-8">
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">Bank-by-bank guardrails</h3>
                <p className="text-sm text-[#0A1F44]/65 mt-1 mb-5">Each lender plays a different game.</p>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-3">
                    {BANK_FOIR_BANDS.map((b) => (
                        <div key={b.name} data-testid={`aff-bank-${b.name.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-2xl bg-white border border-[#0A1F44]/8 p-4">
                            <div className="flex items-baseline justify-between">
                                <div className="font-semibold text-[#0A1F44]">{b.name}</div>
                                <div className="text-[11px] text-[#0A1F44]/55 tabular-nums">{b.rate.toFixed(2)}% p.a.</div>
                            </div>
                            <div className="mt-2 font-display font-extrabold text-3xl text-[#0A1F44] tabular-nums">{b.rule}%</div>
                            <div className="text-[11px] text-[#0A1F44]/55">FOIR ceiling</div>
                        </div>
                    ))}
                </div>
                <p data-testid="aff-bank-disclaimer" className="mt-4 text-[11px] text-[#0A1F44]/55 italic">{BANK_DISCLAIMER}</p>
            </div>
        </Reveal>
    );
}

function EMIPlanner() {
    const { targetEMI, setTargetEMI, affordabilityOutputs } = useHomeIQ();
    const eligibleEMI = Math.round(affordabilityOutputs.emi_on_eligible_loan);
    const showHint = targetEMI <= eligibleEMI;
    return (
        <Reveal delay={0.05}>
            <div data-testid="aff-emi-planner" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">Plan around an EMI you can live with</h3>
                <p className="text-sm text-[#0A1F44]/65 mt-1 mb-5">Slide to a number that feels honest.</p>
                <div className="flex items-baseline gap-3">
                    <span className="text-[#0A1F44]/50">₹</span>
                    <input
                        type="number"
                        value={targetEMI}
                        onChange={(e) => setTargetEMI(Number(e.target.value) || 0)}
                        data-testid="aff-emi-input"
                        className="bg-transparent text-4xl font-display font-extrabold text-[#0A1F44] focus:outline-none w-48"
                    />
                    <span className="text-sm text-[#0A1F44]/55">per month</span>
                </div>
                <input
                    type="range"
                    min={10000}
                    max={200000}
                    step={1000}
                    value={targetEMI}
                    onChange={(e) => setTargetEMI(Number(e.target.value))}
                    data-testid="aff-emi-slider"
                    className="mt-4 w-full accent-[#1E6BFF]"
                />
                <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                        { k: "Loan possible", v: fmtINRCompact(affordabilityOutputs.target_loan) },
                        { k: "Home value", v: fmtINRCompact(affordabilityOutputs.target_home_value) },
                        { k: "Debt-Free Earlier By", v: `${affordabilityOutputs.years_saved.toFixed(1)} yrs` },
                    ].map((s) => (
                        <div key={s.k} className="rounded-2xl bg-[#F7F8FB] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{s.k}</div>
                            <div className="mt-1 font-display font-extrabold text-2xl text-[#0A1F44]">{s.v}</div>
                        </div>
                    ))}
                </div>
                {showHint && (
                    <p
                        data-testid="aff-emi-hint"
                        className="mt-3 text-[11px] text-[#0A1F44]/65 italic"
                    >
                        Slide above ₹{eligibleEMI.toLocaleString("en-IN")} (your eligible EMI) to see how much earlier you&apos;d be debt-free.
                    </p>
                )}
            </div>
        </Reveal>
    );
}

function RepaymentSummary() {
    const { affordabilityOutputs, loanOutputs, setActiveSection, setScrollTargetId } = useHomeIQ();
    const { eligible_loan, total_interest, total_repayment } = affordabilityOutputs;
    // Year at which cumulative principal first crosses 50% of loan — live from amortisation.
    const half = loanOutputs.equity_milestones.find((m) => m.pct === 50);
    const yearsToHalf = half && half.months != null ? (half.months / 12).toFixed(1) : "—";
    const rows = [
        { k: "Principal", v: fmtINRCompact(eligible_loan) },
        { k: "Total interest", v: fmtINRCompact(total_interest) },
        { k: "Total outflow", v: fmtINRCompact(total_repayment) },
        { k: "Years to half-paid", v: typeof yearsToHalf === "string" && yearsToHalf !== "—" ? `${yearsToHalf} yrs` : "—" },
    ];
    const total = eligible_loan + total_interest;
    const principalPct = total > 0 ? Math.round((eligible_loan / total) * 100) : 0;
    const interestPct = 100 - principalPct;
    const prepaySaved = loanOutputs.prepayment.interest_saved;
    const { lump, frequency } = loanOutputs.prepayment;
    const onSeePlan = () => {
        setActiveSection("loan");
        setScrollTargetId("loan-prepayment-simulator");
    };
    return (
        <Reveal delay={0.1}>
            <div data-testid="aff-repayment-summary" className="rounded-3xl border border-[#0A1F44]/10 bg-white p-6 md:p-8">
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44]">What you&apos;ll actually repay</h3>
                <p className="text-sm text-[#0A1F44]/65 mt-1 mb-5">Across the full tenure.</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {rows.map((r) => (
                        <div key={r.k} className="rounded-2xl bg-[#F7F8FB] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">{r.k}</div>
                            <div className="mt-1 font-display font-extrabold text-2xl text-[#0A1F44]">{r.v}</div>
                        </div>
                    ))}
                </div>

                {/* Principal vs Interest split */}
                <div data-testid="aff-principal-interest-split" className="mt-6">
                    <div className="flex items-baseline justify-between mb-2 text-xs">
                        <span className="text-[10px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold">Principal vs interest</span>
                        <span className="text-[#0A1F44]/55">Of total outflow</span>
                    </div>
                    <div className="h-3 rounded-full overflow-hidden flex">
                        <div className="h-full bg-[#0A1F44]" style={{ width: `${principalPct}%` }} aria-label="Principal" />
                        <div className="h-full bg-[#dc2626]" style={{ width: `${interestPct}%` }} aria-label="Interest" />
                    </div>
                    <div className="mt-2 flex justify-between text-xs">
                        <span className="text-[#0A1F44] font-semibold">Principal {principalPct}%</span>
                        <span className="text-[#dc2626] font-semibold">Interest {interestPct}%</span>
                    </div>
                </div>

                <div className="mt-5 rounded-2xl bg-[#0A1F44] text-white p-5 flex items-center justify-between flex-wrap gap-3">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.25em] text-white/55 font-semibold">Prepayment can save</div>
                        <div data-testid="aff-prepay-saved" className="mt-1 font-display font-extrabold text-3xl tabular-nums">{fmtINR(prepaySaved)}</div>
                        <div className="text-[11px] text-white/55 mt-0.5">
                            Based on {fmtINRCompact(lump)} · {frequency.toLowerCase()}
                        </div>
                    </div>
                    <button
                        onClick={onSeePlan}
                        data-testid="aff-prepay-cta"
                        className="px-4 py-2 rounded-full bg-[#1E6BFF] hover:bg-white hover:text-[#0A1F44] text-xs font-semibold transition-colors"
                    >
                        See plan →
                    </button>
                </div>
            </div>
        </Reveal>
    );
}

function Insights() {
    const lines = [
        "You're using only 41% of your FOIR — room to negotiate a better rate.",
        "Stretching to 60% adds ₹15L to your budget but adds 3 yrs of EMI.",
        "Adding ₹3L to down payment cuts ₹2,800 from monthly EMI.",
    ];
    return (
        <Reveal delay={0.05}>
            <div data-testid="aff-insights" className="rounded-3xl border border-[#0A1F44]/10 bg-[#F7F8FB] p-6 md:p-8">
                <p className="text-[11px] tracking-[0.3em] uppercase text-[#1E6BFF] font-semibold mb-2">— What we&apos;d say if you asked</p>
                <h3 className="font-display font-extrabold text-2xl text-[#0A1F44] mb-5">Insights</h3>
                <ul className="space-y-3">
                    {lines.map((l, i) => (
                        <li key={i} className="flex gap-3 text-[#0A1F44]">
                            <span className="text-[#1E6BFF] font-bold">{String(i + 1).padStart(2, "0")}</span>
                            <span>{l}</span>
                        </li>
                    ))}
                </ul>
            </div>
        </Reveal>
    );
}

// ────────────────────────────────────────────────────────────────
// Sticky summary strip — pinned only while inputs are scrolled past
// and the big Results Strip has not yet reached the viewport top.
// ────────────────────────────────────────────────────────────────
const STATUS_TONES = {
    Healthy: { dot: "#16a34a", text: "text-[#16a34a]" },
    Review: { dot: "#d97706", text: "text-[#d97706]" },
    "Action Needed": { dot: "#dc2626", text: "text-[#dc2626]" },
};

function StickySummary() {
    const { affordabilityOutputs } = useHomeIQ();
    const {
        max_affordable_property,
        emi_on_eligible_loan,
        segment,
        foir_status,
        is_affordability_alert,
    } = affordabilityOutputs;
    const reducedMotion = useReducedMotion();
    const [inputsPassed, setInputsPassed] = useState(false);
    const [resultsReached, setResultsReached] = useState(false);

    useEffect(() => {
        const inputsEl = document.querySelector('[data-testid="aff-controls-panel"]');
        const resultsEl = document.querySelector('[data-testid="aff-results-strip"]');
        if (!inputsEl || !resultsEl) return undefined;

        // Trigger line — top of viewport plus the height of Header + DecisionDashboard.
        const triggerOffset = 128;

        const inputsObs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    setInputsPassed(!e.isIntersecting && e.boundingClientRect.bottom < triggerOffset);
                });
            },
            { rootMargin: `-${triggerOffset}px 0px 0px 0px`, threshold: 0 }
        );
        const resultsObs = new IntersectionObserver(
            (entries) => {
                entries.forEach((e) => {
                    // True only while Results Strip is currently visible in the usable viewport.
                    setResultsReached(e.isIntersecting);
                });
            },
            { rootMargin: `-${triggerOffset}px 0px 0px 0px`, threshold: 0 }
        );

        inputsObs.observe(inputsEl);
        resultsObs.observe(resultsEl);
        return () => {
            inputsObs.disconnect();
            resultsObs.disconnect();
        };
    }, []);

    const pinned = inputsPassed && !resultsReached;

    let status = "Healthy";
    if (is_affordability_alert) status = foir_status === "Critical" ? "Action Needed" : "Review";
    const tone = STATUS_TONES[status];

    const duration = reducedMotion ? 0 : 0.2;

    return (
        <AnimatePresence>
            {pinned && (
                <motion.div
                    initial={{ opacity: 0, y: -8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -8 }}
                    transition={{ duration, ease: "easeOut" }}
                    data-testid="aff-sticky-summary"
                    className="fixed left-0 right-0 top-28 md:top-32 z-20 px-6 md:px-12 pointer-events-none"
                >
                    <div className="max-w-7xl mx-auto pointer-events-auto rounded-2xl border border-[#0A1F44]/10 bg-white/95 backdrop-blur-sm shadow-[0_8px_24px_-12px_rgba(10,31,68,0.18)] grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3 px-4 py-2 md:py-3">
                        <div data-testid="aff-sticky-property" className="min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold truncate">Affordable Property</div>
                            <div className="font-display font-extrabold text-base md:text-lg text-[#0A1F44] tabular-nums truncate">{fmtINRCompact(max_affordable_property)}</div>
                        </div>
                        <div data-testid="aff-sticky-emi" className="min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold truncate">EMI</div>
                            <div className="font-display font-extrabold text-base md:text-lg text-[#0A1F44] tabular-nums truncate">₹{Math.round(emi_on_eligible_loan).toLocaleString("en-IN")}</div>
                        </div>
                        <div data-testid="aff-sticky-segment" className="min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold truncate">Segment</div>
                            <div className="font-display font-extrabold text-base md:text-lg text-[#0A1F44] truncate">{segment}</div>
                        </div>
                        <div data-testid="aff-sticky-status" className="min-w-0">
                            <div className="text-[9px] uppercase tracking-[0.25em] text-[#0A1F44]/55 font-semibold truncate">Status</div>
                            <div className={`font-display font-extrabold text-base md:text-lg truncate ${tone.text}`}>
                                <span className="inline-block w-2 h-2 rounded-full mr-2 align-middle" style={{ backgroundColor: tone.dot }} aria-hidden />
                                {status}
                            </div>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

export default function AffordabilityTab() {
    return (
        <section data-testid="affordability-tab" className="py-14 px-6 md:px-12 bg-white">
            <StickySummary />
            <div className="max-w-7xl mx-auto space-y-10">
                <SectionHead
                    eyebrow="Affordability"
                    title="What can you actually afford?"
                    sub="Built on FOIR — the only number a lender truly cares about. Slide the inputs, watch the truth move."
                />
                <SalaryRow />
                <BankRuleToggle />
                <ControlsPanel />
                <AffordabilityAlert />
                <ResultsStrip />
                <div className="grid md:grid-cols-2 gap-6">
                    <FOIRStatus />
                    <CostBreakdown />
                </div>
                <FOIRComparisonTable />
                <BankBands />
                <div className="grid md:grid-cols-2 gap-6">
                    <EMIPlanner />
                    <RepaymentSummary />
                </div>
                <Insights />
            </div>
        </section>
    );
}
